/**
 * Promise utilities for handling timeouts and unhandled rejections
 */

/**
 * Creates a promise that rejects with a proper Error after a timeout
 * @param {Promise} promise - The promise to wrap
 * @param {number} timeoutMs - Timeout in milliseconds
 * @param {string} errorMessage - Custom error message
 * @returns {Promise} - Wrapped promise with timeout
 */
export const withTimeout = (promise, timeoutMs = 30000, errorMessage = 'Operation timed out') => {
    const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
            reject(new Error(`${errorMessage} (${timeoutMs}ms)`));
        }, timeoutMs);
    });

    return Promise.race([promise, timeoutPromise]);
};

/**
 * Wraps a promise to ensure it always resolves or rejects with a proper Error
 * @param {Promise} promise - The promise to wrap
 * @param {string} context - Context for error messages
 * @returns {Promise} - Wrapped promise that always rejects with Error
 */
export const ensureErrorRejection = (promise, context = 'Unknown operation') => {
    return promise.catch(error => {
        // If it's already an Error, re-throw it
        if (error instanceof Error) {
            throw error;
        }
        
        // If it's a string or other value, wrap it in an Error
        if (typeof error === 'string') {
            throw new Error(`${context}: ${error}`);
        }
        
        // For other values (like 'Timeout'), create a proper Error
        throw new Error(`${context}: ${String(error)}`);
    });
};

/**
 * Creates a timeout promise that rejects with a proper Error
 * @param {number} timeoutMs - Timeout in milliseconds
 * @param {string} operation - Operation name for error message
 * @returns {Promise} - Promise that rejects after timeout
 */
export const createTimeoutPromise = (timeoutMs, operation = 'Operation') => {
    return new Promise((_, reject) => {
        setTimeout(() => {
            reject(new Error(`${operation} timed out after ${timeoutMs}ms`));
        }, timeoutMs);
    });
};

/**
 * Wraps a function to ensure it returns a promise that rejects with proper Error
 * @param {Function} fn - Function to wrap
 * @param {string} context - Context for error messages
 * @returns {Function} - Wrapped function
 */
export const wrapAsyncFunction = (fn, context = 'Async operation') => {
    return async (...args) => {
        try {
            const result = await fn(...args);
            return result;
        } catch (error) {
            // Ensure error is always an Error object
            if (error instanceof Error) {
                throw error;
            }
            throw new Error(`${context}: ${String(error)}`);
        }
    };
};

/**
 * Sets up global unhandled rejection handler
 */
export const setupUnhandledRejectionHandler = () => {
    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
        console.error('Unhandled Promise Rejection:', event.reason);
        
        // If the rejection value is not an Error, convert it
        if (!(event.reason instanceof Error)) {
            let errorMessage;
            
            // Handle objects with code and message properties (like wallet errors)
            if (event.reason && typeof event.reason === 'object' && event.reason.code && event.reason.message) {
                errorMessage = `Unhandled promise rejection (${event.reason.code}): ${event.reason.message}`;
            } else if (event.reason && typeof event.reason === 'object') {
                // Handle other objects by stringifying them
                errorMessage = `Unhandled promise rejection: ${JSON.stringify(event.reason)}`;
            } else {
                // Handle primitives
                errorMessage = `Unhandled promise rejection: ${String(event.reason)}`;
            }
            
            // Create error for logging - don't modify event.reason as it's read-only in Firefox
            const error = new Error(errorMessage);
        }
        
        // Check for specific authentication errors
        const reasonStr = typeof event.reason === 'string' ? event.reason : event.reason?.message || '';
        const isAuthError = reasonStr && 
                           (reasonStr.includes('Invalid authentication cookie') ||
                            reasonStr.includes('generate_auth_cookie') ||
                            reasonStr.includes('authentication') ||
                            reasonStr.includes('cookie'));
        
        // Check for translation service errors
        const isTranslationError = reasonStr && 
                                 (reasonStr.includes('translate_http') ||
                                  reasonStr.includes('sw/<(') ||
                                  reasonStr.includes('translate.goog'));
        
        if (isAuthError) {
            console.warn('Authentication error detected in promise rejection:', reasonStr);
            
            // Handle authentication errors gracefully
            const authError = event.reason instanceof Error ? event.reason : new Error(reasonStr);
            handleAuthenticationError(authError, { 
                redirect: false,  // Don't auto-redirect, let user see what happened
                showNotification: false  // Don't show alert, just log
            });
        }
        
        if (isTranslationError) {
            console.warn('Translation service error detected in promise rejection:', event.reason.message);
            
            // Suppress translation errors as they're from third-party services
            event.preventDefault();
            return;
        }
        
        // Log to Sentry if available
        if (window.Sentry) {
            window.Sentry.captureException(event.reason, {
                tags: {
                    errorType: isAuthError ? 'AUTHENTICATION_ERROR' : 'UNHANDLED_PROMISE_REJECTION',
                    rejectionValue: String(event.reason)
                }
            });
        }
        
        // Prevent the default behavior (which would log to console)
        event.preventDefault();
    });
};

/**
 * Handles authentication errors gracefully
 * @param {Error} error - The authentication error
 */
export const handleAuthenticationError = (error) => {
    try {
        // Clear invalid authentication data
        if (typeof window !== 'undefined') {
            // Clear cookies that might be invalid
            const cookiesToClear = ['rbuser', 'current_resume', 'auth_token'];
            cookiesToClear.forEach(cookieName => {
                try {
                    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.resume.org`;
                    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
                } catch (e) {
                    console.warn(`Could not clear cookie ${cookieName}:`, e);
                }
            });
            
            // Clear localStorage authentication data
            if (window.localStorage) {
                try {
                    const authKeys = ['user', 'auth', 'token', 'session'];
                    authKeys.forEach(key => {
                        if (localStorage.getItem(key)) {
                            localStorage.removeItem(key);
                        }
                    });
                } catch (e) {
                    console.warn('Could not clear localStorage auth data:', e);
                }
            }
            
            // Redirect to login if not already there
            if (!window.location.pathname.includes('/login') && 
                !window.location.pathname.includes('/signup')) {
                console.log('Redirecting to login due to authentication error');
                window.location.href = '/login';
            }
        }
    } catch (e) {
        console.error('Error handling authentication error:', e);
    }
};

/**
 * Creates a debounced promise that only resolves after a delay
 * @param {Function} fn - Function to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} - Debounced function that returns a promise
 */
export const debouncePromise = (fn, delay = 300) => {
    let timeoutId;
    let lastPromise;
    
    return (...args) => {
        return new Promise((resolve, reject) => {
            clearTimeout(timeoutId);
            
            timeoutId = setTimeout(async () => {
                try {
                    // If there's a pending promise, wait for it
                    if (lastPromise) {
                        await lastPromise;
                    }
                    
                    lastPromise = fn(...args);
                    const result = await lastPromise;
                    resolve(result);
                } catch (error) {
                    reject(error);
                }
            }, delay);
        });
    };
};

/**
 * Retries a promise with exponential backoff
 * @param {Function} fn - Function that returns a promise
 * @param {number} maxRetries - Maximum number of retries
 * @param {number} baseDelay - Base delay in milliseconds
 * @returns {Promise} - Promise that retries on failure
 */
export const retryPromise = async (fn, maxRetries = 3, baseDelay = 1000) => {
    let lastError;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            return await fn();
        } catch (error) {
            lastError = error;
            
            if (attempt === maxRetries) {
                throw error;
            }
            
            // Exponential backoff
            const delay = baseDelay * Math.pow(2, attempt);
            await new Promise(resolve => setTimeout(resolve, delay));
        }
    }
    
    throw lastError;
};

export default {
    withTimeout,
    ensureErrorRejection,
    createTimeoutPromise,
    wrapAsyncFunction,
    setupUnhandledRejectionHandler,
    debouncePromise,
    retryPromise
};
